import { useMemo, useState } from "react"
import { metaAI } from "neql"
import { FilterFields } from "../Components/Common/FilterComposer/FilterComposer"
import { APIJobQueryModel } from "../Models/API/JobModel"
import { getValue, hasResult, hasValue } from "../Models/Client/FetchState"
import { useAppTemplate } from "./useAppTemplate"
import { useAIModelAssignmentStatus } from "./useAIModelAssignmentStatus"
import { useAppSchema } from "./useAppSchema"
import { useJobResponse } from "./useJobResponse"
import { useAppInstance } from "./useAppInstance"
import {
    filterFeedbackTags,
    getLastCompletedJobID
} from "../Models/API/AppInstanceModel"
import { useJobTotalCount } from "./useJobTotalCount"
import uniqBy from "lodash/uniqBy"
import {
    collectFilterableFields,
    getSerializedPath
} from "../Models/Client/JsonFieldFilter"
import { jobFilterOptions } from "../Components/Views/Dashboard/Sections/AppDetail/Common/constants"
import { useTagList } from "./useTagList"

const MAX_JSON_FILTERABLE_JOBS = 100000 - 1

export function useJobFilterParams(
    appID: string,
    isMessagesPath: boolean
): FilterFields<APIJobQueryModel & { output: unknown }> {
    const templateFetch = useAppTemplate(appID)
    const activeLearningStatusFetch = useAIModelAssignmentStatus(
        appID,
        metaAI.Meta_Ai_Assignment_Enum.ActiveLearning
    )
    const schemaFetch = useAppSchema(appID, false)
    const instanceFetch = useAppInstance(appID)

    const lastCompletedJobOutputFetch = useJobResponse(
        appID,
        hasValue(instanceFetch)
            ? getLastCompletedJobID(instanceFetch.value)
            : undefined
    )
    const totalCount = useJobTotalCount(appID, isMessagesPath)

    const [tagSearchKeyword, setTagSearchKeyword] = useState("")
    const tagsFetch = useTagList(appID, tagSearchKeyword)

    return useMemo(() => {
        if (
            !hasValue(templateFetch) ||
            !hasResult(activeLearningStatusFetch) ||
            !hasValue(schemaFetch)
        ) {
            return {}
        }

        const lastCompletedJobOutput = getValue(lastCompletedJobOutputFetch)
        const canJsonSearch =
            totalCount !== undefined && totalCount <= MAX_JSON_FILTERABLE_JOBS
        const inputFields = canJsonSearch
            ? uniqBy(
                  collectFilterableFields(schemaFetch.value.input, undefined),
                  getSerializedPath
              )
            : []
        const outputFields = canJsonSearch
            ? uniqBy(
                  collectFilterableFields(
                      schemaFetch.value.output,
                      lastCompletedJobOutput?.response
                  ),
                  getSerializedPath
              )
            : []

        return jobFilterOptions(
            tagsFetch,
            inputFields,
            outputFields,
            canJsonSearch,
            setTagSearchKeyword,
            isMessagesPath,
            hasValue(instanceFetch)
                ? filterFeedbackTags(
                      instanceFetch.value?.jobWithFeedbackStats
                          ?.feedbackTagStats
                  )
                : undefined,
            !activeLearningStatusFetch.isError &&
                activeLearningStatusFetch.value !== "unused",
            getValue(instanceFetch)?.canoticCrowd
        )
    }, [
        activeLearningStatusFetch,
        instanceFetch,
        isMessagesPath,
        lastCompletedJobOutputFetch,
        schemaFetch,
        tagsFetch,
        templateFetch,
        totalCount
    ])
}
